import { useState } from 'react'

import { DataItem, useDataItem } from 'scrivito'

const PLACEHOLDER_ID = '00000000000000000000000000000000'

/**
 * Returns the current Scrivito `DataItem` for the page, but retains the last
 * known valid item across Scrivito's transient placeholder state.
 *
 * After `DataItem.update()`, Scrivito briefly emits a placeholder item with
 * ID `00000000000000000000000000000000` while the cache revalidates. Without
 * this hook, components that depend on `useDataItem()` would unmount or blank
 * during that window. This hook holds the previous valid item until the fresh
 * one arrives.
 *
 * @returns The most recent valid `DataItem`, or `undefined` on initial render
 *          before any item has been loaded.
 */
export const useStableDataItem = (): DataItem | undefined => {
  const raw = useDataItem()
  const [last, setLast] = useState<DataItem | undefined>(raw)

  if (raw?.id() && raw.id() !== PLACEHOLDER_ID && raw.id() !== last?.id()) {
    setLast(raw)
  }

  return last
}
